import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { distinctUntilChanged, startWith } from 'rxjs';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { Metadata } from '../../../../api/api-types/api-response.api';
import { ApiDisabilityProofUpdateBody } from '../../../../api/api-types/disability-proof.api';
import { Client } from '../../../../api/app-types/client.app';
import { DisabilityProof } from '../../../../api/app-types/disability-proof.app';
import { DisabilityStatus, DisabilityStatusInitial } from '../../../../api/app-types/disability-status.app';
import { DocumentFileSubjectType } from '../../../../api/app-types/document.app';
import { DISABILITY_PROOF_SERVICE } from '../../../../api/services/disability-proof.service';
import { provideUploadApi } from '../../../../api/services/upload.api';
import { CUSTOM_DATE_FORMATS } from '../../../../core/adapters/custom-date-format';
import { LvrRedirectNav } from '../../../../core/classes/redirect-nav.class';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrHeading } from '../../../../core/components/heading/heading';
import { LvrSearchableSelect } from '../../../../core/components/searchable-select/searchable-select';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { DocumentUploadControl } from '../../../../shared/document-upload/document-upload';
import { allowedFileTypes, maxFiles, maxFileSize } from '../../../../shared/document-upload/document-upload-validators';
import { matDateProviders } from '../../../../shared/helpers/date.func';
import { hasError } from '../../../../shared/helpers/has-error.func';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../shared/notification/notification.factory';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { ClientResolved } from '../../../../shared/routing/resolvers/client.resolver';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { dateRangeValidator } from '../../../../shared/validators/date-range-validator';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';
import { maxDateValidator } from '../../../../shared/validators/max-date-validator';

@Component({
	selector: 'lvr-disability-proof-edit',
	imports: [
		TranslocoPipe,
		DatePipe,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		LvrSearchableSelect,
		MatRadioModule,
		MatTooltip,
		LvrHeading,
		LvrPaddingDirective,
		LvrField,
		LvrAppendAsteriskPipe,
		LvrInputDirective,
		LvrAutoSaveDirective,
		DocumentUploadControl,
		LvrRestoreActions,
		LvrDefaultActions,
	],
	templateUrl: './disability-proof-edit.html',
	styleUrl: './disability-proof-edit.scss',
	providers: [matDateProviders, provideUploadApi],
})
export class DisabilityProofEdit extends LvrRedirectNav {
	public readonly subjectType = DocumentFileSubjectType.DISABILITY_REPORT;
	private readonly env = inject(ENVIRONMENT);
	private readonly data = toSignal(this.route.data);
	private readonly fb = inject(FormBuilder);
	private readonly draftSrv = inject(FormPersistenceService);
	private readonly session = inject(SessionManagerService);
	private readonly api = inject(DISABILITY_PROOF_SERVICE);
	private readonly ntfySrv = inject(NOTIFICATION_SERVICE);
	private readonly transloco = inject(TranslocoService);
	public readonly isLoading = signal<boolean>(false);
	public readonly proof = computed(() => this.data()?.['proof'].data as DisabilityProof);
	private readonly metadata = computed(() => this.data()!['proof'].metadata as Metadata);
	private readonly editableSet = computed(() => new Set(this.metadata().editOnlyFields));
	public readonly hasEditable = computed(() => this.editableSet().size > 0);
	private readonly proofs = computed(() => this.data()!['proofs'] as DisabilityProof[]);
	private readonly takenDates = computed<Set<string>>(() => {
		const currentId = this.proof().id;
		return new Set(
			this.proofs()
				.filter((p) => p.id !== currentId)
				.map((p) => p.since.toISOString()),
		);
	});
	public readonly disabilityStatus = computed(() => this.statusData().entities);
	private readonly statusData = computed(() => this.data()!['status'] as PaginatedResponse<DisabilityStatus>);
	private readonly sbStatusDurationRequired = new Set<DisabilityStatusInitial>([
		DisabilityStatusInitial.SEVERELY_DISABLED,
		DisabilityStatusInitial.EQUALIZED,
	]);

	private readonly clientResolved = computed<ClientResolved | undefined>(() => {
		return this.data()?.['client'] as ClientResolved | undefined;
	});
	public readonly client = computed<Client>(() => this.clientResolved()?.value as Client);
	public readonly form = this.initForm();
	public get PROOF_SETTINGS() {
		return this.env.proofSettings;
	}
	public readonly maxRemarkLength = this.PROOF_SETTINGS.maxRemarkLength;
	public readonly dateFormatHint = this.env.visualDateFormat;
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly maxFileSize = this.env.fileSettings.maxFileSize;
	public readonly maxOtherTextLength = this.env.maxTitleLength ?? 50;
	public readonly draftId = computed(() => FORM_KEYS.UPDATE_DISABILITY_PROOF + '.' + this.proof().id);
	public readonly matcher = new ImmediateErrorStateMatcher();
	public hasError = hasError;
	public readonly canEditDisabilityReportsPermission = hasPermission(
		this.session.permissions,
		'Documents.DisabilityReport.Edit',
	);

	private readonly _selectedStatus = toSignal(
		this.form.get('sbStatus')!.valueChanges.pipe(startWith(this.form.get('sbStatus')!.value), distinctUntilChanged()),
	);

	public readonly gdBValues = computed(() => {
		const selectedStatus = this._selectedStatus();
		if (!selectedStatus) return [];
		return this.disabilityStatus().find((status) => status.id === selectedStatus)?.relatedDisabilitySeverities ?? [];
	});

	public readonly remainingDocumentSlots = computed(() => {
		return Math.max(0, this.PROOF_SETTINGS.maxFiles - this.proof().documents.length);
	});

	private readonly sbStatusCtrl = this.form.controls.sbStatus;
	private readonly otherTextCtrl = this.form.controls.otherText;
	private readonly sbStatusValue = toSignal(this.sbStatusCtrl.valueChanges, { initialValue: this.sbStatusCtrl.value });
	public readonly selectedSbStatus = computed(() => {
		const selectedId = this.sbStatusValue();

		if (selectedId == null) {
			return undefined;
		}

		return this.disabilityStatus().find((item) => item.id === selectedId);
	});
	public readonly shouldDisplayOtherText = computed(() => {
		return this.selectedSbStatus()?.initial === DisabilityStatusInitial.OTHER;
	});

	public constructor() {
		super();
		this.setupForm();

		// Disable non-editable controls if the Proof status is frozen
		this.disableFormCtrls();

		this.handleOtherTextField();
	}

	private handleOtherTextField() {
		effect(() => {
			const needsOtherText = this.shouldDisplayOtherText();
			if (needsOtherText) {
				this.otherTextCtrl.addValidators([Validators.required, Validators.maxLength(this.maxOtherTextLength)]);
			} else {
				this.otherTextCtrl.clearValidators();
				if (this.otherTextCtrl.value) this.otherTextCtrl.setValue('', { emitEvent: false });
			}
			this.otherTextCtrl.updateValueAndValidity({ emitEvent: false });
		});
	}

	/**
	 * Determines whether a form field can be edited based on the
	 * 'editOnlyFields' array provided by the metadata.
	 * @param formField The name of the form field.
	 * @returns True if the editable set has no value (not frozen),
	 * or it has a value and has the form field; false otherwise.
	 */
	public canEdit(formField: string): boolean {
		// If there are editable fields defined, check if this one is included
		// Otherwise, default to true (all fields editable)
		return this.hasEditable() ? this.editableSet().has(formField) : true;
	}

	/**
	 * Disables all form controls except those listed in the editable set.
	 * Runs when the editable set is defined and the Proof status is frozen.
	 */
	private disableFormCtrls() {
		Object.keys(this.form.controls).forEach((key) => {
			const editable = this.canEdit(key);
			const control = this.form.get(key);

			if (editable) {
				control?.enable({ emitEvent: false });
			} else {
				control?.disable({ emitEvent: false });
			}
		});
	}

	// Returns the maximum date for the SB-Ausweis
	// It can be max. today
	public get maxSbProofDate() {
		const today = new Date();
		return today;
	}

	// Returns the maximum date for the SB-Ausweis
	// It can be max. today + 50 years.
	public get maxLimitedDate() {
		const today = new Date();
		today.setFullYear(today.getFullYear() + this.PROOF_SETTINGS.maxExpiryYears);
		return today;
	}

	public get minLimitedDate() {
		const start = this.form.get('dateSbProof')?.value;
		return start ? new Date(start) : null;
	}

	public shouldDurationBeOptional() {
		const selectedStatus = this.selectedSbStatus();

		return !selectedStatus || !this.sbStatusDurationRequired.has(selectedStatus.initial as DisabilityStatusInitial);
	}

	public hasFileError(control: AbstractControl | null, ...errors: string[]) {
		if (!control) return '';

		const error = errors.find(
			(error) => control.hasError(error) && (control.dirty || !!control.value?.length || control.touched),
		);

		if (!error) return '';
		return error;
	}

	// Get radio button value from limitedUntil date existence
	public getLimitedRadio(lu: Date | undefined) {
		return lu ? '2' : '1';
	}

	/**
	 * Handles form cancellation, deletes draft and navigates to detail view.
	 */
	public async cancel() {
		this.draftSrv.deleteDraft('client', this.draftId());
		await this.navigateAfterFinished(['/desk/client', this.client()!.id, 'disabilities', 'proofs', this.proof().id]);
	}

	/**
	 * Handles disability proof update, deletes draft and navigates to detail view.
	 */
	public async update() {
		if (this.isLoading()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const proof = this.proof();
		const fv = this.form.value;
		const mergedDocs = this.hasEditable() ? [...(this.proof().documents ?? []), ...(fv.documents ?? [])] : fv.documents;

		const body: ApiDisabilityProofUpdateBody = {
			id: proof.id,
			remark: fv.remark ?? '',
			documentIds: mergedDocs?.map((doc) => doc.id) ?? [],
		};

		if (this.hasEditable()) {
			body.clientId = this.client().id;
			body.statusStart = proof.since.getISODateStringWithoutTime();
			body.statusEnd = proof.status.limitedUntil?.getISODateStringWithoutTime();
			body.categoryDisabilityStatusId = proof.categoryDisabilityStatus.id;
			body.categoryDisabilitySeverityId = proof.categoryDisabilitySeverity.id;
			body.otherText = proof.otherText;
		} else {
			body.clientId = this.client().id;
			body.statusStart = fv.dateSbProof?.getISODateStringWithoutTime();
			body.statusEnd = fv.limitedUntil?.getISODateStringWithoutTime();
			body.categoryDisabilityStatusId = fv.sbStatus ?? undefined;
			body.categoryDisabilitySeverityId = fv.gdbValue ?? undefined;
			body.otherText = fv.otherText ?? undefined;
		}

		this.isLoading.set(true);

		try {
			await this.api.update(body);
			await this.cancel();
			this.ntfySrv.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.disability_proof_updated')),
			);
		} catch {
			this.ntfySrv.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.disability_proof_update_error')),
			);
		} finally {
			this.isLoading.set(false);
		}
	}

	private initForm() {
		const canEditFiles = this.canEdit('documents');
		const editable = this.hasEditable();

		const initialDocs = canEditFiles && editable ? [] : (this.proof().documents ?? []);
		const maxFilesLimit =
			canEditFiles && editable
				? this.PROOF_SETTINGS.maxFiles - this.proof().documents.length
				: this.PROOF_SETTINGS.maxFiles;

		const formGroup = this.fb.group({
			dateSbProof: [this.proof().since, [Validators.required, maxDateValidator(this.maxSbProofDate)]],
			sbStatus: [this.proof().categoryDisabilityStatus.id, [Validators.required]],
			limited: [this.getLimitedRadio(this.proof().status.limitedUntil)],
			limitedUntil: [this.proof().status.limitedUntil],
			gdbValue: [this.proof().categoryDisabilitySeverity.id, [Validators.required]],
			remark: [this.proof().remark, [Validators.maxLength(this.maxRemarkLength)]],
			otherText: [this.proof().otherText],
			documents: [
				initialDocs,
				[
					maxFiles(maxFilesLimit),
					maxFileSize(this.env.fileSettings.maxFileSize),
					allowedFileTypes(this.env.fileSettings.allowedMimeTypes),
				],
			],
		});

		this.dateRangeValidator(formGroup);
		this.sbStatusDurationRequiredValidator(formGroup);

		return formGroup;
	}

	/**
	 * Adds dateSbProof control 'takenDate' validator and resets gdbValue control on status change.
	 */
	private setupForm() {
		// Add validator
		this.form.controls.dateSbProof.addValidators(this.takenDateValidator());

		effect(() => {
			const selectedStatusId = this._selectedStatus();
			const options = this.gdBValues();
			const gdbCtrl = this.form.controls.gdbValue;

			if (!selectedStatusId) gdbCtrl.reset(undefined, { emitEvent: false });

			const allowedIds = new Set(options.map((o) => o.id));
			const gdbValueId = gdbCtrl.value as number | null | undefined;
			const stillValid = gdbValueId != null && allowedIds.has(gdbValueId);

			if (!stillValid) {
				gdbCtrl.reset(undefined, { emitEvent: false });
				gdbCtrl.markAsPristine();
				gdbCtrl.markAsUntouched();
			}

			gdbCtrl.updateValueAndValidity({ emitEvent: false });
		});
	}

	/**
	 * Validator for knowing whether a Date has been already used for creating a Disability Proof.
	 * @returns `true` if a value is in the `takenDates` set.
	 */
	private takenDateValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			if (!value) return null;
			const isoDateStr = new Date(value).toISOString();
			return this.takenDates().has(isoDateStr) ? { dateTaken: true } : null;
		};
	}

	private dateRangeValidator(formGroup: FormGroup) {
		const sbDateCtrl = formGroup.get('dateSbProof');
		const limitedUntilCtrl = formGroup.get('limitedUntil');

		const applyValidator = () => {
			limitedUntilCtrl?.addValidators([
				dateRangeValidator(sbDateCtrl, limitedUntilCtrl),
				maxDateValidator(this.maxLimitedDate),
			]);
			limitedUntilCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
		};

		limitedUntilCtrl?.valueChanges.pipe(startWith(limitedUntilCtrl.value), takeUntilDestroyed()).subscribe(() => {
			applyValidator();
		});

		sbDateCtrl?.valueChanges.pipe(startWith(sbDateCtrl.value), takeUntilDestroyed()).subscribe(() => {
			applyValidator();
		});

		applyValidator();
	}

	private sbStatusDurationRequiredValidator(formGroup: FormGroup) {
		const sbCtrl = formGroup.get('sbStatus');
		const limitedCtrl = formGroup.get('limited');
		const limitedUntilCtrl = formGroup.get('limitedUntil');

		const applyValidator = () => {
			const selectedStatusId = Number(sbCtrl?.value);
			const selectedStatus = this.disabilityStatus().find((status) => status.id === selectedStatusId);

			const requiresDuration =
				!!selectedStatus && this.sbStatusDurationRequired.has(selectedStatus.initial as DisabilityStatusInitial);

			if (requiresDuration) {
				limitedCtrl?.setValidators([Validators.required]);
			} else {
				limitedCtrl?.clearValidators();
				if (limitedCtrl?.value) {
					limitedCtrl.setValue('', { emitEvent: false });
				}
			}
			limitedCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });

			const validatorsForLimitedUntil = [maxDateValidator(this.maxLimitedDate)];
			if (requiresDuration && limitedCtrl?.value === '2') {
				validatorsForLimitedUntil.unshift(Validators.required);
			}
			limitedUntilCtrl?.setValidators(validatorsForLimitedUntil);

			if (!(requiresDuration && limitedCtrl?.value === '2') && limitedUntilCtrl?.value != null) {
				limitedUntilCtrl.setValue(undefined, { emitEvent: false });
			}

			limitedUntilCtrl?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
		};

		sbCtrl?.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed()).subscribe(() => applyValidator());

		limitedCtrl?.valueChanges
			.pipe(startWith(limitedCtrl.value), takeUntilDestroyed())
			.subscribe(() => applyValidator());

		applyValidator();
	}
}
